import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  CreditCard, Search, Ban, RefreshCw, Eye, CheckCircle, XCircle,
  Clock, AlertTriangle, TrendingUp, ArrowUpDown, Pencil, DollarSign,
  Pause, Play, FileText, X,
} from 'lucide-react';

/* ============================================================
   REASON MODAL
   ============================================================ */
function ReasonModal({ title, label, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 9999 }}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="sa-form-group">
            <label className="sa-form-label">{label || 'Reason'}</label>
            <textarea className="sa-input" rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Enter reason..." autoFocus />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onConfirm(reason)}>Confirm</button>
        </div>
      </div>
    </div>
  );
}


const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);

const STATUS_CONFIG = {
  active: { className: 'sa-badge-active', label: 'Active', icon: CheckCircle },
  trialing: { className: 'sa-badge-trialing', label: 'Trial', icon: Clock },
  pending: { className: 'sa-badge-pending', label: 'Pending', icon: AlertTriangle },
  past_due: { className: 'sa-badge-pending', label: 'Past Due', icon: AlertTriangle },
  expired: { className: 'sa-badge-expired', label: 'Expired', icon: XCircle },
  cancelled: { className: 'sa-badge-cancelled', label: 'Cancelled', icon: Ban },
  suspended: { className: 'sa-badge-suspended', label: 'Suspended', icon: Pause },
};

export default function SuperAdminSubscriptions() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modal, setModal] = useState(null); // { type: 'suspend'|'cancel', subId, coName }


  const load = useCallback(async () => {
    try {
      const c = await subscriptionsAPI.companies({ search, status: statusFilter || undefined });
      setCompanies(c);
    } catch { /* */ }
  }, [search, statusFilter]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    const timer = setTimeout(() => load(), 300);
    return () => clearTimeout(timer);
  }, [search, statusFilter]);

  const handleCancelConfirm = async (reason) => {
    if (!modal) return;
    try {
      await subscriptionsAPI.cancel(modal.subId, { reason: reason || 'Cancelled by admin' });
      toast.success('Subscription cancelled');
      setModal(null);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleReactivate = async (subId) => {
    try {
      await subscriptionsAPI.reactivate(subId);
      toast.success('Subscription reactivated');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleConfirm = async (subId, paymentConfirmed) => {
    try {
      await subscriptionsAPI.confirm(subId, { payment_confirmed: paymentConfirmed });
      toast.success(paymentConfirmed ? 'Subscription confirmed & payment received!' : 'Subscription confirmed!');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleSuspendConfirm = async (reason) => {
    if (!modal) return;
    try {
      await subscriptionsAPI.suspend(modal.subId, { reason: reason || 'Suspended by admin' });
      toast.success('Subscription suspended');
      setModal(null);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleUnsuspend = async (subId) => {
    try {
      await subscriptionsAPI.unsuspend(subId);
      toast.success('Subscription unsuspended');
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleConfirmWithPrompt = async (co) => {
    const paid = window.confirm(
      `Confirm subscription for "${co.name}"?\n\n` +
      `Plan: ${co.plan_name}\n` +
      `Status: Will become ACTIVE\n\n` +
      `Click OK = Payment received (mark as paid)\n` +
      `Click Cancel = Confirm without payment`
    );
    await handleConfirm(co.sub_id, paid);
  };

  const filters = [
    { key: '', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'trialing', label: 'Trialing' },
    { key: 'pending', label: 'Pending' },
    { key: 'suspended', label: 'Suspended' },
    { key: 'past_due', label: 'Past Due' },
    { key: 'expired', label: 'Expired' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  // Count per status
  const counts = {};
  companies.forEach(c => {
    const s = c.sub_status || 'none';
    counts[s] = (counts[s] || 0) + 1;
  });

  return (
    <div>
      {/* Summary stats */}
      <div className="sa-stats-grid" style={{ marginBottom: 16 }}>
        {[
          { label: 'Total', value: companies.length, color: '#3b82f6' },
          { label: 'Active', value: counts.active || 0, color: '#16a34a' },
          { label: 'Trialing', value: counts.trialing || 0, color: '#8b5cf6' },
          { label: 'Pending', value: counts.pending || 0, color: '#f59e0b' },
          { label: 'Suspended', value: counts.suspended || 0, color: '#6366f1' },
          { label: 'Expired/Cancelled', value: (counts.expired || 0) + (counts.cancelled || 0), color: '#ef4444' },
        ].map((s, i) => (
          <div key={i} className="sa-stat-card" onClick={() => setStatusFilter(s.label === 'Total' ? '' : s.label === 'Expired/Cancelled' ? 'expired' : s.label.toLowerCase())} style={{ cursor: 'pointer' }}>
            <div className="sa-stat-icon" style={{ background: s.color, width: 36, height: 36 }}>
              <CreditCard size={16} />
            </div>
            <div className="sa-stat-body">
              <div className="sa-stat-label">{s.label}</div>
              <div className="sa-stat-value" style={{ fontSize: 20 }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="sa-toolbar">
        <div className="sa-toolbar-left">
          <div className="sa-search-box" style={{ width: 260 }}>
            <Search size={14} />
            <input placeholder="Search subscriptions..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="sa-filter-tabs">
            {filters.map(f => (
              <button key={f.key} className={`sa-filter-tab ${statusFilter === f.key ? 'active' : ''}`} onClick={() => setStatusFilter(f.key)}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="sa-card">
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Billing</th>
                <th>Revenue</th>
                <th>Period</th>
                <th>Days Left</th>
                <th className="sa-text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="sa-empty-row"><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              ) : companies.length === 0 ? (
                <tr className="sa-empty-row"><td colSpan={8}>No subscriptions found</td></tr>
              ) : companies.map(co => {
                const st = STATUS_CONFIG[co.sub_status] || STATUS_CONFIG.expired;
                const endDate = co.current_period_end || co.trial_ends_at;
                const daysLeft = endDate ? Math.max(0, Math.ceil((new Date(endDate) - new Date()) / 86400000)) : null;

                return (
                  <tr key={co.id}>
                    <td>
                      <div className="sa-company-cell">
                        <strong>{co.name}</strong>
                        <span>{co.email}</span>
                      </div>
                    </td>
                    <td style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>{co.plan_name || 'No Plan'}</td>
                    <td><span className={`sa-badge-status ${st.className}`}>{st.label}</span></td>
                    <td style={{ fontSize: 12, textTransform: 'capitalize' }}>{co.billing_cycle || '—'}</td>
                    <td style={{ fontSize: 12, fontWeight: 600 }}>{co.sub_status === 'active' ? fmt(co.price_monthly) + '/mo' : '—'}</td>
                    <td style={{ fontSize: 11 }}>{fmtDate(co.current_period_start)} — {fmtDate(endDate)}</td>
                    <td>
                      {daysLeft !== null ? (
                        <span style={{
                          fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                          background: daysLeft <= 7 ? '#fef2f2' : daysLeft <= 30 ? '#fffbeb' : '#f0fdf4',
                          color: daysLeft <= 7 ? '#991b1b' : daysLeft <= 30 ? '#92400e' : '#166534',
                        }}>
                          {daysLeft}d
                        </span>
                      ) : '—'}
                    </td>
                    <td className="sa-text-right">
                      <div className="sa-row-actions">
                        {/* View company */}
                        <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => navigate(`/super-admin/companies/${co.id}`)} title="View Company">
                          <Eye size={12} />
                        </button>

                        {/* Confirm pending */}
                        {co.sub_status === 'pending' && co.sub_id && (
                          <button className="sa-btn sa-btn-primary sa-btn-sm" onClick={() => handleConfirmWithPrompt(co)} title="Confirm Subscription">
                            <CheckCircle size={12} /> Confirm
                          </button>
                        )}

                        {/* Pay for trialing */}
                        {co.sub_status === 'trialing' && co.sub_id && (
                          <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => handleConfirmWithPrompt(co)} title="Confirm & Pay" style={{ borderColor: '#16a34a', color: '#16a34a' }}>
                            <DollarSign size={12} /> Pay
                          </button>
                        )}

                        {/* Suspend active */}
                        {(co.sub_status === 'active' || co.sub_status === 'trialing') && co.sub_id && (
                          <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => setModal({ type: 'suspend', subId: co.sub_id, coName: co.name })} title="Suspend" style={{ color: '#6366f1', borderColor: '#6366f1' }}>
                            <Pause size={12} />
                          </button>
                        )}

                        {/* Unsuspend */}
                        {co.sub_status === 'suspended' && co.sub_id && (
                          <button className="sa-btn sa-btn-success sa-btn-sm" onClick={() => handleUnsuspend(co.sub_id)} title="Unsuspend">
                            <Play size={12} /> Unsuspend
                          </button>
                        )}

                        {/* Cancel active */}
                        {co.sub_status === 'active' && co.sub_id && (
                          <button className="sa-btn sa-btn-danger sa-btn-sm" onClick={() => setModal({ type: 'cancel', subId: co.sub_id, coName: co.name })} title="Cancel">
                            <Ban size={12} />
                          </button>
                        )}

                        {/* Reactivate cancelled/expired */}
                        {(co.sub_status === 'cancelled' || co.sub_status === 'expired') && co.sub_id && (
                          <button className="sa-btn sa-btn-success sa-btn-sm" onClick={() => handleReactivate(co.sub_id)} title="Reactivate">
                            <RefreshCw size={11} />
                          </button>
                        )}

                        {/* Invoice + Pay */}
                        {co.sub_id && (co.sub_status === 'active' || co.sub_status === 'trialing' || co.sub_status === 'pending') && (
                          <>
                            <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={() => navigate('/super-admin/invoices/' + co.sub_id)} title="View & Edit Invoice" style={{ color: '#3b82f6', borderColor: '#3b82f6' }}>
                              <FileText size={12} /> Invoice
                            </button>
                            <button className="sa-btn sa-btn-primary sa-btn-sm" onClick={() => navigate('/super-admin/payments/' + co.sub_id)} title="Process Payment">
                              <CreditCard size={12} /> Pay
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Suspend/Cancel Modal */}
      {modal && (
        <ReasonModal
          title={modal.type === 'suspend' ? 'Suspend ' + modal.coName : "Cancel " + modal.coName + "'s Subscription"}
          label={modal.type === 'suspend' ? 'Suspension reason' : 'Cancellation reason'}
          onConfirm={modal.type === 'suspend' ? handleSuspendConfirm : handleCancelConfirm}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
